import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Box, Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PrimaryButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import boardsStepImage from "@shared/images/BoardsStep.webp";
import customDeviceImage from "@shared/images/CustomDevice.webp";
import tabletPhoneImage from "@shared/images/TabletPhone.webp";

import { PageContainer } from "../../shared/components/PageContainer";
import type { EnrollmentPath } from "../EnrollmentPage";

const tileScale = 0.9;
const cardBorderRadius = Math.round(16 * tileScale);
const cardMediaAspectRatio = "1956 / 804";

const useStyles = makeStyles()((theme: Theme) => {
    const isDark = theme.palette.mode === "dark";

    return {
        container: {
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2.5),
        },
        cardGrid: {
            display: "grid",
            gap: theme.spacing(3),
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            alignItems: "stretch",
            [theme.breakpoints.down("md")]: {
                gridTemplateColumns: "1fr",
            },
        },
        optionCard: {
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            width: "100%",
            height: "100%",
            minHeight: 0,
            gap: 0,
            padding: 0,
            overflow: "hidden",
            borderRadius: cardBorderRadius,
            border: "none",
            backgroundColor: theme.palette.emteria.text.inverse,
            boxShadow: theme.shadows[1],
            cursor: "pointer",
            textAlign: "left",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 32px rgba(0, 0, 0, 0.14)",
            },
            "&:focus-visible": {
                outline: `2px solid ${theme.palette.emteria.action}`,
                outlineOffset: 2,
            },
        },
        cardMedia: {
            width: "100%",
            aspectRatio: cardMediaAspectRatio,
            flexShrink: 0,
            overflow: "hidden",
            backgroundColor: "#B8CDFB",
        },
        cardMediaCustomDevice: {
            backgroundColor: "#F5DF9A",
        },
        cardMediaImage: {
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
        },
        supportMedia: {
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            aspectRatio: cardMediaAspectRatio,
            flexShrink: 0,
            overflow: "hidden",
            backgroundColor: isDark ? theme.palette.emteria.background.paperDeep : "#E4EAF2",
        },
        supportMediaCircle: {
            position: "absolute",
            right: -44,
            top: "50%",
            width: 225,
            height: 225,
            borderRadius: "50%",
            transform: "translateY(-50%)",
            backgroundColor: isDark ? alpha(theme.palette.common.white, 0.04) : "#D5DEE9",
            pointerEvents: "none",
        },
        supportIconRow: {
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1.5),
        },
        supportIconTile: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 58,
            height: 58,
            borderRadius: 13,
            backgroundColor: theme.palette.common.white,
            boxShadow: "0 4px 14px rgba(4, 44, 76, 0.12)",
            color: theme.palette.emteria.primary,
            fontSize: 22,
        },
        cardBody: {
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: theme.spacing(1.5),
            flex: 1,
            width: "100%",
            padding: theme.spacing(3, 3.5),
            boxSizing: "border-box",
            backgroundColor: theme.palette.emteria.text.inverse,
        },
        optionTitle: {
            fontWeight: 700,
            fontSize: 18,
            lineHeight: 1.3,
            color: theme.palette.emteria.primary,
            textAlign: "left",
        },
        optionDescription: {
            color: theme.palette.emteria.text.secondary,
            fontSize: 14,
            lineHeight: 1.45,
        },
        cardFooter: {
            marginTop: "auto",
            alignSelf: "stretch",
            display: "flex",
            justifyContent: "flex-end",
            paddingTop: theme.spacing(0.5),
        },
    };
});

type EnrollmentOption = {
    path: EnrollmentPath;
    title: string;
    description: string;
    buttonLabel: string;
    mediaBackground?: string;
    mediaClassName?: string;
    mediaIcons?: string[];
    outlinedButton?: boolean;
};

interface EnrollmentTypeSelectionStepProps {
    onSelect: (path: EnrollmentPath) => void;
}

type EnrollmentOptionCardProps = {
    option: EnrollmentOption;
    onSelect: (path: EnrollmentPath) => void;
    classes: ReturnType<typeof useStyles>["classes"];
};

const EnrollmentOptionCard = ({ option, onSelect, classes }: EnrollmentOptionCardProps) => {
    const handleSelect = () => {
        onSelect(option.path);
    };

    const actionButton = option.outlinedButton ? (
        <PrimaryOutlinedButton
            name={`enrollment-option-${option.path}`}
            onClick={(event) => {
                event.stopPropagation();
                handleSelect();
            }}
        >
            {option.buttonLabel}
        </PrimaryOutlinedButton>
    ) : (
        <PrimaryButton
            name={`enrollment-option-${option.path}`}
            onClick={(event) => {
                event.stopPropagation();
                handleSelect();
            }}
        >
            {option.buttonLabel}
        </PrimaryButton>
    );

    return (
        <Box className={classes.optionCard} onClick={handleSelect}>
            {option.mediaIcons && (
                <Box className={classes.supportMedia} aria-hidden>
                    <Box className={classes.supportMediaCircle} />
                    <Box className={classes.supportIconRow}>
                        {option.mediaIcons.map((iconClassName) => (
                            <Box key={iconClassName} className={classes.supportIconTile}>
                                <i className={iconClassName} />
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
            {option.mediaBackground && (
                <Box className={clsx(classes.cardMedia, option.mediaClassName)} aria-hidden>
                    <img
                        src={option.mediaBackground}
                        alt=""
                        width={1956}
                        height={804}
                        className={classes.cardMediaImage}
                        loading="eager"
                        decoding="sync"
                    />
                </Box>
            )}
            <Box className={classes.cardBody}>
                <Typography component="h3" className={classes.optionTitle}>
                    {option.title}
                </Typography>
                <Typography className={classes.optionDescription}>{option.description}</Typography>
                <Box className={classes.cardFooter}>{actionButton}</Box>
            </Box>
        </Box>
    );
};

export const EnrollmentTypeSelectionStep: React.FC<EnrollmentTypeSelectionStepProps> = ({ onSelect }) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals"]);

    const enrollmentOptions: EnrollmentOption[] = [
        {
            path: "install_android_os",
            title: t("modals:enrollmentDialog.enrollFirstDevice.cards.installAndroidOs.title"),
            description: t("modals:enrollmentDialog.enrollFirstDevice.cards.installAndroidOs.description"),
            buttonLabel: t("modals:enrollmentDialog.enrollFirstDevice.cards.installAndroidOs.button"),
            mediaBackground: boardsStepImage,
        },
        {
            path: "install_mdm",
            title: t("modals:enrollmentDialog.enrollFirstDevice.cards.installMdm.title"),
            description: t("modals:enrollmentDialog.enrollFirstDevice.cards.installMdm.description"),
            buttonLabel: t("modals:enrollmentDialog.enrollFirstDevice.cards.installMdm.button"),
            mediaBackground: tabletPhoneImage,
        },
        {
            path: "enroll_existing",
            title: t("modals:enrollmentDialog.enrollFirstDevice.cards.enrollExisting.title"),
            description: t("modals:enrollmentDialog.enrollFirstDevice.cards.enrollExisting.description"),
            buttonLabel: t("modals:enrollmentDialog.enrollFirstDevice.cards.enrollExisting.button"),
            mediaBackground: customDeviceImage,
            mediaClassName: classes.cardMediaCustomDevice,
        },
        {
            path: "build_custom",
            title: t("modals:enrollmentDialog.enrollFirstDevice.cards.buildCustom.title"),
            description: t("modals:enrollmentDialog.enrollFirstDevice.cards.buildCustom.description"),
            buttonLabel: t("modals:enrollmentDialog.enrollFirstDevice.cards.buildCustom.button"),
            mediaIcons: ["fa-solid fa-wrench", "fa-regular fa-comment", "fa-regular fa-circle-question"],
            outlinedButton: true,
        },
    ];

    return (
        <PageContainer fullWidth={false}>
            <Box className={classes.cardGrid}>
                {enrollmentOptions.map((option) => (
                    <EnrollmentOptionCard key={option.path} option={option} onSelect={onSelect} classes={classes} />
                ))}
            </Box>
        </PageContainer>
    );
};
